"use client";

import React, { useState, ChangeEvent, FormEvent } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Popover,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import { AlertColor } from "@mui/material/Alert";

interface PopoverFormProps {
  triggerElement: React.ReactNode;
}

interface FormData {
  fullName: string;
  company: string;
  countryCode: string;
  mobileNumber: string;
  email: string;
  message: string;
}

interface Notification {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const PopoverForm: React.FC<PopoverFormProps> = ({ triggerElement }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    company: "",
    countryCode: "",
    mobileNumber: "",
    email: "",
    message: "",
  });

  const [notification, setNotification] = useState<Notification>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation rules
    if (
      !formData.fullName.trim() ||
      !formData.company.trim() ||
      !formData.countryCode.trim() ||
      !formData.mobileNumber.trim() ||
      !formData.email.trim() ||
      !formData.message.trim()
    ) {
      setNotification({
        open: true,
        message: "Please fill out all required fields.",
        severity: "error",
      });
      return;
    }

    // Email Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setNotification({
        open: true,
        message: "Please enter a valid email address.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch("/api/sendEmail", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          message: formData.message,
          company: formData.company,
          countryCode: formData.countryCode,
          mobileNumber: formData.mobileNumber,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setNotification({
          open: true,
          message: "Email sent successfully!",
          severity: "success",
        });

        // Clear form
        setFormData({
          fullName: "",
          company: "",
          countryCode: "",
          mobileNumber: "",
          email: "",
          message: "",
        });

        handleClose();
      } else {
        setNotification({
          open: true,
          message: "Failed to send email. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      setNotification({
        open: true,
        message: "An error occurred while sending the email. Please try again.",
        severity: "error",
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <div>
      <Box onClick={handleOpen} sx={{ display: "inline-block" }}>
        {triggerElement}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference="none"
        PaperProps={{
          style: {
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            overflowY: "auto",
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: "16px",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            width: isSmallMobile ? "95vw" : isMobile ? "90vw" : "80vw",
            maxWidth: "1200px",
            maxHeight: "90vh",
          },
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
            backdropFilter: "blur(10px)",
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={0}>
              {/* Left Side - Contact Info - Hidden on mobile */}
              {!isMobile && (
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      color: "white",
                      p: { xs: 3, md: 4 },
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      minHeight: "500px",
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 3,
                        fontSize: { xs: "1.75rem", md: "2.5rem" },
                      }}
                    >
                      Let&apos;s Connect
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}
                    >
                      Share your vision with us, and let&apos;s turn your ideas
                      into reality—together. We&apos;re here to listen and help
                      you achieve your goals.
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        📞 Phone
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        +233 20 033 5078
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        ✉️ Email
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        info@linxarlabs.com
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Right Side - Form */}
              <Grid size={{ xs: 12, md: isMobile ? 12 : 7 }}>
                <Box
                  sx={{
                    p: { xs: 3, sm: 4 },
                    position: "relative",
                    minHeight: isMobile ? "auto" : "500px",
                  }}
                >
                  <IconButton
                    onClick={handleClose}
                    aria-label="close"
                    sx={{
                      position: "absolute",
                      right: { xs: 8, sm: 16 },
                      top: { xs: 8, sm: 16 },
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {isMobile && (
                    <Box
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        color: "white",
                        p: 3,
                        borderRadius: 2,
                        mb: 3,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: "1.5rem",
                        }}
                      >
                        Let&apos;s Connect
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Share your vision with us
                      </Typography>
                    </Box>
                  )}

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    Get In Touch
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Full Name *"
                        type="text"
                        name="fullName"
                        fullWidth
                        value={formData.fullName}
                        onChange={handleChange}
                        variant="outlined"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Company / Individual *"
                        type="text"
                        name="company"
                        fullWidth
                        value={formData.company}
                        onChange={handleChange}
                        variant="outlined"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Country Code *"
                        type="text"
                        name="countryCode"
                        fullWidth
                        value={formData.countryCode}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="+233"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Phone Number *"
                        type="tel"
                        name="mobileNumber"
                        fullWidth
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        variant="outlined"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={12}>
                      <TextField
                        label="Email *"
                        type="email"
                        name="email"
                        fullWidth
                        value={formData.email}
                        onChange={handleChange}
                        variant="outlined"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={12}>
                      <TextField
                        label="Message *"
                        name="message"
                        fullWidth
                        multiline
                        rows={isSmallMobile ? 3 : 4}
                        value={formData.message}
                        onChange={handleChange}
                        variant="outlined"
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: theme.palette.primary.main,
                            },
                          },
                        }}
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid size={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 2,
                          alignItems: "center",
                          justifyContent: "center",
                          mt: 2,
                        }}
                      >
                        <Button
                          variant="contained"
                          type="submit"
                          size={isSmallMobile ? "medium" : "large"}
                          sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1, sm: 1.5 },
                            borderRadius: 2,
                            fontWeight: 600,
                            color: "white",
                            width: { xs: "100%", sm: "auto" },
                            minWidth: { xs: "100%", sm: "140px" },
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Send Message
                        </Button>

                        <Typography
                          sx={{
                            color: "text.secondary",
                            mx: { xs: 0, sm: 2 },
                            my: { xs: 1, sm: 0 },
                            display: "block",
                          }}
                        >
                          or
                        </Typography>

                        <Button
                          variant="outlined"
                          onClick={handleConsult}
                          size={isSmallMobile ? "medium" : "large"}
                          sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1, sm: 1.5 },
                            borderRadius: 2,
                            fontWeight: 600,
                            width: { xs: "100%", sm: "auto" },
                            minWidth: { xs: "100%", sm: "180px" },
                            "&:hover": {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.04
                              ),
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          Book a Consultation
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Popover>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{
            width: "100%",
            borderRadius: 2,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PopoverForm;
